/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable security/detect-object-injection */
/* eslint-disable @typescript-eslint/no-floating-promises */
/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    CategoryHierarchy as CategoryHierarchyData,
    IProductRefinerHierarchy,
    IProductsDimensionsAvailabilities
} from '@msdyn365-commerce/commerce-entities';
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import {
    format,
    ProductPrice,
    ProductRefinerValue,
    ProductSearchResult,
    SortColumn,
    TextValueTranslation
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    DimensionAvailabilitiesForProductSearchResultInput,
    DimensionTypes,
    getDimensionAvailabilitiesForProductSearchResultAction,
    getPriceForProductSearchResult,
    IDimensionsApp,
    PriceForProductSearchResultInput,
    getProductPageUrlSync,
    generateImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    LabeledDropdown,
    UncontrolledPagination
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import {
    buildListPageUrl,
    getCollectionProducts,
    getCollectionRefinersAction,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    IFullProductsSearchResultsWithCount,
    parseQueryParam,
    RefinersByCollectionInput,
    sortOptions
} from './actions';
import { hydrateRefinersFromUrl } from './actions/url-utils';
import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    Link,
    ModalToggle,
    ProductSearchResultItems,
    SearchResultModal,
    Separator,
    Title,
    ICuratedCollection
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { IMoeSearchResultContainerData } from './moe-search-result-container.data';
import { expandRefinersCount, IMoeSearchResultContainerProps } from './moe-search-result-container.props.autogenerated';
import { ERROR_OR_NOTIFICATION, HEADER_FOOTER, KWR, SEARCH_SITE_LANGUAGE } from '../../utilities/constants';
import { getToggleLang, getUserPrefLang, getUserPreferredLanguage } from '../../utilities/language-change';
import { FaArrowRight } from 'react-icons/fa';
import { IMOEChannel } from '../../actions/extensions/DataServiceEntities.g';
import { getMOEOnlineStoresAsync, moeSearchProductsAsync } from '../../actions/extensions/DataActionExtension.g';
import { ListPageState } from './list-page-state';

export interface ISearchResultContainerViewProps extends IMoeSearchResultContainerProps<IMoeSearchResultContainerData> {
    products?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    TitleViewProps: ITitleViewProps;
    categoryHierarchy: ICategoryHierarchyViewProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    CategoryNavContainer: INodeProps;
    RefineAndProductSectionContainer: INodeProps;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    curatedCollectionData: ICuratedCollection[];
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    SortingContainer: INodeProps;
    sortByDropDown?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container.
 */
export interface ISearchResultTitle {
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    refiners: IProductRefinerHierarchy[];
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    localSearchText: string;
    resourcesSkip: number;
    ochSkip: number;
    artifactsSkip: number;
    removeResourceShowMore: boolean;
    removeOchShowMore: boolean;
    removeArtifactShowMore: boolean;
    enableListViewToggle?: boolean;
    expandAllRefiners?: string | boolean;
    channelData: IMOEChannel[];
    tahurangiSearchCount: any[];
}

interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}

/**
 *
 * SearchResultContainer component.
 * @extends {React.PureComponent<IMoeSearchResultContainerProps<IMoeSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IMoeSearchResultContainerProps<IMoeSearchResultContainerData>,
    ISearchResultContainerState
> {
    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    // Identifying Language
    private toggleLangStarting = getToggleLang(this.props.context);
    private userPrefLangStarting = getUserPrefLang(this.props.context.request.user.customerAccountNumber as any, this.props.context);
    private preferredSearchSiteLanguageStarting: any = getUserPreferredLanguage(
        KWR,
        SEARCH_SITE_LANGUAGE,
        this.userPrefLangStarting,
        this.toggleLangStarting,
        this.props.context
    );

    private readonly sortByDropdownOptions: ILabeledDropdownOption[] = [
        {
            key: sortOptions.sortByOptionRelevanceDesc,
            value:
                this.preferredSearchSiteLanguageStarting === 'maori'
                    ? this.props.resources.sortByOptionRelevanceDescInMaori
                    : this.props.resources.sortByOptionRelevanceDesc
        },
        {
            key: sortOptions.sortByOptionNewScore,
            value:
                this.preferredSearchSiteLanguageStarting === 'maori'
                    ? this.props.resources.sortByOptionNewScoreInMaori
                    : this.props.resources.sortByOptionNewScore
        },
        {
            key: sortOptions.sortByOptionNameAsc,
            value:
                this.preferredSearchSiteLanguageStarting === 'maori'
                    ? this.props.resources.sortByOptionNameAscInMaori
                    : this.props.resources.sortByOptionNameAsc
        },
        {
            key: sortOptions.sortByOptionNameDesc,
            value:
                this.preferredSearchSiteLanguageStarting === 'maori'
                    ? this.props.resources.sortByOptionNameDescInMaori
                    : this.props.resources.sortByOptionNameDesc
        },
        {
            key: sortOptions.sortByOptionRatingDesc,
            value:
                this.preferredSearchSiteLanguageStarting === 'maori'
                    ? this.props.resources.sortByOptionRatingDescInMaori
                    : this.props.resources.sortByOptionRatingDesc
        }
        // { key: sortOptions.sortByOptionBestSelling, value: this.props.resources.sortByOptionBestSellingInMaori },
        // { key: sortOptions.sortByOptionTrendingScore, value: this.props.resources.sortByOptionTrendingScoreInMaori }
    ];

    private readonly _refineItemCommonProps: IRefineItemCommonProps;

    private readonly _pageType: string | undefined = this.props.context.request.urlTokens.pageType;

    @observable
    private _viewport: GridSize;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private readonly localSearchRef: React.RefObject<any>;

    private readonly refineChild: React.RefObject<any>;

    private readonly _sortAndFilterContainerRef: React.RefObject<LabeledDropdown>;

    private expandrefinerCount: number | undefined = 0;

    private readonly telemetryContent: ITelemetryContent;

    private readonly productPrices: ProductPrice[];

    private readonly defaultPageNumber = 0;

    public constructor(props: IMoeSearchResultContainerProps<IMoeSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        this._viewport =
            props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<LabeledDropdown>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this.productPrices = [];
        this.localSearchRef = React.createRef();
        this.refineChild = React.createRef();
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            refiners: [],
            localSearchText: '',
            resourcesSkip: 0,
            ochSkip: 0,
            artifactsSkip: 0,
            removeResourceShowMore: true,
            removeOchShowMore: true,
            removeArtifactShowMore: true,
            enableListViewToggle: false,
            expandAllRefiners: '',
            channelData: [],
            tahurangiSearchCount: []
        };
        const { placeholderTextMaxInMaori, minLabelInMaori, maxLabelInMaori, rangeNameFormatInMaori } = this.props.resources;

        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumberInMaori;
        const validationErrorRange = this.props.resources.validationErrorNotRangeInMaori;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax: placeholderTextMaxInMaori,
            minLabel: minLabelInMaori,
            maxLabel: maxLabelInMaori,
            rangeNameFormat: rangeNameFormatInMaori,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        // To remove show more button in the starting
        this._getAllArtifactsData({} as any);
        this._updateViewport();
    }

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation?.Text;
    }

    public async componentDidMount(): Promise<void> {
        if (MsDyn365.isBrowser && window.addEventListener) {
            window.addEventListener('resize', this._updateViewport);
            this._updateViewport();
        }
        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
            this._getActivePrice();
        }

        const products = await this.props.data.products;
        const listPageState = await this.props.data.listPageState;

        await this._updateListPageState(listPageState, products);
        reaction(
            () => {
                return [
                    listPageState.activeFilters?.length,
                    // listPageState.currentPageNumber,
                    listPageState.sortingCritera
                    // ArrayExtensions.hasElements(listPageState.sortingCritera?.Columns),
                    // listPageState.sortingCritera?.Columns
                ];
            },
            async () => {
                const newListPageState = listPageState;
                const input = new GetFullProductsByCollectionInput(
                    newListPageState.pageType,
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage,
                            Skip: Math.max(newListPageState.pageSize * (newListPageState.currentPageNumber ?? 0), 0)
                        },
                        count: true,
                        Sorting: newListPageState.sortingCritera ?? {}
                    },
                    newListPageState.activeFilters ?? [],
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                    +(this.props.context.request.urlTokens.recordId || 0),

                    // eslint-disable-next-line max-len -- existing code
                    this.props.context.request.query?.q
                        ? this.props.context.request.query.q
                        : JSON.stringify({
                              ProductId: this.props.context.request.query?.productId,
                              Recommendation: this.props.context.request.query?.recommendation
                          }),
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Required check.
                    !!this.props?.config?.includeAttributes,
                    this.props.config.updateRefinerPanel,
                    this.props.context.request.locale
                );

                const productResults = await getCollectionProducts(input, this.props.context.actionContext);
                newListPageState.totalProductCount = this._getProductsCount(productResults?.products[0]);
                newListPageState.activeProducts = productResults.products;

                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- check is required.
                if (
                    this.props.config.enableAffiliationBasedPricing &&
                    this.props.context.actionContext.requestContext?.user?.isAuthenticated
                ) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Call api to retrieve the availabilities data without blocking the UI.
                    this._getActivePrice();
                }

                if (this.props.config.updateRefinerPanel) {
                    const refinerInput = new RefinersByCollectionInput(
                        newListPageState.pageType,
                        this.props.context.request.apiSettings,
                        {
                            Paging: {
                                Top: this.props.config.itemsPerPage,
                                Skip: Math.max(
                                    newListPageState.pageSize * (newListPageState.currentPageNumber ?? this.defaultPageNumber),
                                    this.defaultPageNumber
                                )
                            },
                            count: true,
                            Sorting: newListPageState.sortingCritera ?? {}
                        },
                        newListPageState.activeFilters ?? [],
                        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- RecordId is string. Check required.
                        +(this.props.context.request.urlTokens.recordId || 0),
                        this.props.context.request.query?.q
                            ? this.props.context.request.query.q
                            : this.props.context.request.query?.productId,
                        false,
                        this.props.config.updateRefinerPanel,
                        undefined
                    );

                    const refiners = await getCollectionRefinersAction(refinerInput, this.props.context.actionContext);
                    this.setState({ refiners });
                }
            }
        );
    }

    private readonly _updateListPageState = async (listPageState: ListPageState, products: IFullProductsSearchResultsWithCount) => {
        if (this._pageType === 'Category') {
            listPageState.pageType = 'Category';
        } else {
            listPageState.pageType = 'Search';
        }

        let querySorting: SortColumn[] = [];

        if (this.props.context.request.query && this.props.context.request.query.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query && +this.props.context.request.query.skip / (this.props.config.itemsPerPage || 10)) || 0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage || 10;
        listPageState.activeProducts = products.products;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);
        listPageState.artifactsData = this._getAllArtifactsData(products?.products[0]);
        listPageState.totalProductCount = this._getProductsCount(products?.products[0]);
        if (
            listPageState.activeProducts.length <
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (listPageState.currentPageNumber + 1) * (listPageState.pageSize || 10)
        ) {
            this.setState({
                removeOchShowMore: true,
                removeResourceShowMore: true,
                removeArtifactShowMore: true
            });
        } else if (listPageState.activeProducts.length > (listPageState.currentPageNumber + 1) * (listPageState.pageSize || 10)) {
            if (this.state.removeResourceShowMore) {
                this.setState({
                    removeOchShowMore: false,
                    removeResourceShowMore: false,
                    removeArtifactShowMore: false
                });
            }
        }

        // Construct Base Search URL
        let searchTitle: string | undefined;
        const { context, data } = this.props;

        if (context && context.request && context.request.query && context.request.query.q) {
            searchTitle = context.request.query.q;
        } else {
            searchTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }

        const searchRequest = moeSearchProductsAsync(
            { callerContext: this.props.context.actionContext, bypassCache: 'get' },
            {
                Context: { ChannelId: this.props.context.request.channel?.RecordId, CatalogId: 0 },
                IncludeAttributes: true,
                Language: this.props.context.request?.locale,
                Refinement: listPageState.activeFilters || [],
                SearchCondition: searchTitle,
                SkipVariantExpansion: true
            }
        )
            .then(channelCountResponse => {
                console.log('channelCountResponse---==', channelCountResponse);
                this.setState({ ...this.state, tahurangiSearchCount: channelCountResponse });
            })
            .catch(e => {
                console.error('search count error', e);
            });

        const storeRequest = getMOEOnlineStoresAsync({ callerContext: this.props.context.actionContext, bypassCache: 'get' }).then(
            channelResponse => {
                this.setState({ ...this.state, channelData: channelResponse });
            }
        );

        const dimensionsToDisplayOnProductCard = context.app.config.dimensionsInProductCard;
        const dimensionsToDisplayAsSwatch = context.app.config.dimensionsAsSwatchType;
        let getDimensionsAvailabilitiesRequest = Promise.resolve();
        if (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            ArrayExtensions.hasElements(dimensionsToDisplayAsSwatch) &&
            !dimensionsToDisplayAsSwatch.includes(DimensionTypes.none) &&
            this.props.config.includeAttributes
        ) {
            getDimensionsAvailabilitiesRequest = this._getProductsDimensionsAvailabilities();
        }

        if (listPageState.activeProducts.length < listPageState?.totalProductCount) {
            if (this.state.removeResourceShowMore) {
                this.setState({
                    removeResourceShowMore: false
                });
            }
            if (this.state.removeOchShowMore) {
                this.setState({
                    removeOchShowMore: false
                });
            }
            if (this.state.removeArtifactShowMore) {
                this.setState({
                    removeArtifactShowMore: false
                });
            }
        }

        // execute 3 requests in parallel and await together
        await Promise.all([searchRequest, storeRequest, getDimensionsAvailabilitiesRequest]).catch(err => {
            console.log(err.message);
        });
    };

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser && window.removeEventListener) {
            window.removeEventListener('resize', this._updateViewport);
        }
    }

    public render(): JSX.Element {
        const { imageSettings, className, siteName } = this.props.config;
        const { resources, context } = this.props;
        const products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const searchProduct = (this.props.data.listPageState.result && this.props.data.listPageState.result.featureProduct) || null;
        let errorText: any = '';

        // Identifying Language
        const toggleLang = getToggleLang(context);
        const userPrefLang = getUserPrefLang(context.request.user.customerAccountNumber as any, context);
        const preferredLanguageForError: any = getUserPreferredLanguage(
            siteName as any,
            ERROR_OR_NOTIFICATION,
            userPrefLang,
            toggleLang,
            context
        );
        const preferredSearchSiteLanguage: any = getUserPreferredLanguage(
            siteName as any,
            SEARCH_SITE_LANGUAGE,
            userPrefLang,
            toggleLang,
            context
        );
        const preferredLanguageForButton: any = getUserPreferredLanguage(siteName as any, HEADER_FOOTER, userPrefLang, toggleLang, context);

        if (!products || products.length === 0) {
            errorText =
                this._pageType === 'Category' ? (
                    <div className='msc-alert msc-alert-warning'>No resources found for the selected category.</div>
                ) : (
                    <div className='msc-alert msc-alert-warning'>
                        {preferredLanguageForError === 'maori'
                            ? resources.resultSearchNotFoundTextInMaori
                            : resources.resultSearchNotFoundText}
                        .
                    </div>
                );
        }
        const productsComponent = this._getProducts(preferredLanguageForError);
        const ochProductsComponent = this._getOchProducts(preferredLanguageForError, preferredSearchSiteLanguage);
        const ListViewProductsComponent = this._getListViewProducts(preferredLanguageForError);
        const curatedCollectionData = this._getCuratedCollectionsData(preferredLanguageForError);
        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
                recommendation={this.props.context.request.query && this.props.context.request.query?.recommendation}
            />
        );

        console.log('MOE Search Results', this.props);

        const searchResultContainerViewProps = {
            ...this.props,
            products: productsComponent,
            ochProducts: ochProductsComponent,
            productsListView: ListViewProductsComponent,
            localSearchBar: this._getLocalSearchBar(),
            TitleViewProps: this._getCollectionTitle(preferredSearchSiteLanguage),
            categoryHierarchy: !this.props.config.disableHierarchy && this._getCategoryHierarchy(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container', className)
            },
            sortByOptions:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0
                    ? this._getSortingDropDown()
                    : null,
            ministryPagination: this._getMinistryPagination(),
            ochPagination: this._getOchPagination(),
            externalPagination: this._getExternalPagination(),

            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            CategoryNavContainer: { className: 'ms-search-result-container__category-nav-section' },
            RefineAndProductSectionContainer: { className: 'ms-search-result-container__refine-product-section' },
            choiceSummary: this._getChoiceSummary(),
            modalToggle:
                this.props.data.listPageState.result && this.props.data.listPageState.result.totalProductCount !== 0 ? (
                    <ModalToggle
                        text={preferredSearchSiteLanguage === 'maori' ? resources.modalTitleInMaori : resources.modalTitle} //fix: bug 103592 - add site lang for modal title
                        ariaLabel={preferredSearchSiteLanguage === 'maori' ? resources.modalTitleInMaori : resources.modalTitle} //fix: bug 103592 - add site lang for modal title
                        innerRef={this._modalToggleRef}
                        onClick={this._toggleModal}
                        id='search-result-modal'
                    />
                ) : null,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct:
                this.props.data.listPageState.result &&
                this.props.data.listPageState.result.featureProduct !== null &&
                this.props.data.listPageState.result.featureProduct !== undefined
                    ? featureComponent
                    : null,
            productPrices: this.productPrices,
            curatedCollectionData: curatedCollectionData,
            resourceButton: this._getResourcesShowmoreBtn(preferredLanguageForButton),
            ochResourceButton: this._getOchShowmoreBtn(),
            allArtifactsButton: this._getAllartifactsShowmoreBtn(preferredLanguageForButton),
            enableListViewToggle: this.state.enableListViewToggle,
            expandCollapseAllButtons: this._renderCollapseExpandAll(preferredSearchSiteLanguage),
            preferredLanguageForError: preferredLanguageForError,
            preferredSearchSiteLanguage: preferredSearchSiteLanguage,
            preferredLanguageForButton: preferredLanguageForButton
        };
        return this.props.renderView(searchResultContainerViewProps) as React.ReactElement;
    }

    /**
     * Get Resources show more button.
     * @returns Void.
     */
    private readonly _getResourcesShowmoreBtn = (preferredLanguageForButton: string): React.ReactNode => {
        return (
            !this.state.removeResourceShowMore && (
                <div className='resources-showmore-container'>
                    <button onClick={this._addMoreResources} className='moe-btn moe-btn--primary-blue'>
                        {preferredLanguageForButton === 'maori'
                            ? this.props.resources.showMoreTextInMaori
                            : this.props.resources.showMoreText}
                    </button>
                </div>
            )
        );
    };

    private readonly _addMoreResources = async () => {
        const listPageState = await this.props.data.listPageState;
        // const currentResources = [...listPageState.activeProducts];
        const input = new GetFullProductsByCollectionInput(
            listPageState.pageType,
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: this.props.config.itemsPerPage || 10,
                    Skip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10)
                },
                count: true,
                Sorting: listPageState.sortingCritera || {}
            },
            listPageState.activeFilters || [],
            +(this.props.context.request.urlTokens.itemId || 0),

            this.props.context.request.query && this.props.context.request.query.q
                ? this.props.context.request.query.q
                : JSON.stringify({
                      ProductId: this.props.context.request.query?.productId,
                      Recommendation: this.props.context.request.query?.recommendation
                  }),
            !!(this.props && this.props.config && this.props.config.includeAttributes !== undefined && this.props.config.includeAttributes),
            this.props.config.updateRefinerPanel,
            this.props.context.request.locale
        );
        getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
            listPageState.artifactsData = [...listPageState.artifactsData, ...this._getAllArtifactsData(productResults?.products[0])];
            listPageState.activeProducts = [...listPageState.activeProducts, ...productResults.products];
            listPageState.currentPageNumber =
                listPageState.currentPageNumber !== null && listPageState.currentPageNumber !== undefined
                    ? listPageState.currentPageNumber + 1
                    : 0;
            // listPageState.totalProductCount = listPageState.activeProducts.length;
            if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
                this._getActivePrice();
            }
            if (productResults?.products?.length === 0) {
                this.setState({ removeArtifactShowMore: true, removeOchShowMore: true, removeResourceShowMore: true });
            }
            if (listPageState.activeProducts.length >= this._getProductsCount(productResults?.products[0])) {
                this.setState({
                    removeResourceShowMore: true,
                    removeArtifactShowMore: true
                });
            } else if (listPageState.activeProducts.length < this._getProductsCount(productResults?.products[0])) {
                // if (this.state.removeResourceShowMore) {
                this.setState({
                    removeResourceShowMore: false,
                    removeArtifactShowMore: false
                });
                // }
            }
            console.log('List page active products', listPageState.activeProducts);
        });

        this.setState({ resourcesSkip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10) });
    };

    /**
     * Get OCH show more button.
     * @returns Void.
     */
    private readonly _getOchShowmoreBtn = (): React.ReactNode => {
        return (
            !this.state.removeOchShowMore && (
                <div className='och-showmore-container'>
                    <button onClick={this._addOchMoreResources} className='moe-btn moe-btn--primary-blue'>
                        Show More
                    </button>
                </div>
            )
        );
    };

    private readonly _addOchMoreResources = async () => {
        const listPageState = await this.props.data.listPageState;
        // const currentResources = [...listPageState.activeProducts];
        const input = new GetFullProductsByCollectionInput(
            listPageState.pageType,
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: this.props.config.itemsPerPage || 10,
                    Skip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10)
                },
                count: true,
                Sorting: listPageState.sortingCritera || {}
            },
            listPageState.activeFilters || [],
            +(this.props.context.request.urlTokens.itemId || 0),

            this.props.context.request.query && this.props.context.request.query.q
                ? this.props.context.request.query.q
                : JSON.stringify({
                      ProductId: this.props.context.request.query?.productId,
                      Recommendation: this.props.context.request.query?.recommendation
                  }),
            !!(this.props && this.props.config && this.props.config.includeAttributes !== undefined && this.props.config.includeAttributes),
            this.props.config.updateRefinerPanel,
            this.props.context.request.locale
        );
        getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
            listPageState.artifactsData = [...listPageState.artifactsData, ...this._getAllArtifactsData(productResults?.products[0])];
            listPageState.activeProducts = [...productResults.products];
            listPageState.currentPageNumber =
                listPageState.currentPageNumber !== null && listPageState.currentPageNumber !== undefined
                    ? listPageState.currentPageNumber + 1
                    : 0;
            // listPageState.totalProductCount = listPageState.activeProducts.length;
            if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
                this._getActivePrice();
            }
            if (productResults?.products?.length === 0) {
                this.setState({ removeArtifactShowMore: true, removeOchShowMore: true, removeResourceShowMore: true });
            }
            console.log('List page active products', listPageState.activeProducts);
        });

        this.setState({ resourcesSkip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10) });
    };

    /**
     * Get All Artifacts show more button.
     * @returns Void.
     */
    private readonly _getAllartifactsShowmoreBtn = (preferredLanguageForButton: string): React.ReactNode => {
        return (
            !this.state.removeArtifactShowMore && (
                <div className='artifacts-showmore-container'>
                    <button onClick={this._addAllartifactsMoreResources} className='moe-btn moe-btn--primary-blue'>
                        {preferredLanguageForButton === 'maori'
                            ? this.props.resources.showMoreTextInMaori
                            : this.props.resources.showMoreText}
                    </button>
                </div>
            )
        );
    };

    private readonly _addAllartifactsMoreResources = async () => {
        const listPageState = await this.props.data.listPageState;
        const currentResources = [...listPageState.activeProducts];
        const input = new GetFullProductsByCollectionInput(
            listPageState.pageType,
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: this.props.config.itemsPerPage,
                    Skip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10)
                },
                count: true,
                Sorting: listPageState.sortingCritera || {}
            },
            listPageState.activeFilters || [],
            +(this.props.context.request.urlTokens.itemId || 0),

            this.props.context.request.query && this.props.context.request.query.q
                ? this.props.context.request.query.q
                : JSON.stringify({
                      ProductId: this.props.context.request.query?.productId,
                      Recommendation: this.props.context.request.query?.recommendation
                  }),
            !!(this.props && this.props.config && this.props.config.includeAttributes !== undefined && this.props.config.includeAttributes),
            this.props.config.updateRefinerPanel,
            this.props.context.request.locale
        );
        getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
            listPageState.artifactsData = [...listPageState.artifactsData, ...this._getAllArtifactsData(productResults?.products[0])];
            listPageState.activeProducts = [...currentResources, ...productResults.products];
            // listPageState.totalProductCount = listPageState.activeProducts.length;
            if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
                this._getActivePrice();
            }
            if (productResults?.products?.length === 0) {
                this.setState({ removeArtifactShowMore: true, removeOchShowMore: true, removeResourceShowMore: true });
            }
            console.log(productResults);
        });
        this.setState({ resourcesSkip: this.state.resourcesSkip + (this.props.config.itemsPerPage || 10) });
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly _getAllArtifactsData = (firstProduct?: ProductSearchResult): any[] => {
        if (firstProduct && firstProduct.ExtensionProperties && firstProduct.ExtensionProperties[0]) {
            const index: number = firstProduct.ExtensionProperties?.findIndex((p: any) => p.Key === 'ArtifactSearchList');
            if (index > -1) {
                const artifactSearchListString: string = firstProduct.ExtensionProperties[index].Value?.StringValue || '';
                if (!artifactSearchListString) {
                    if (this.state.removeArtifactShowMore === false) {
                        this.setState({ removeArtifactShowMore: true });
                    }
                    return [];
                }
                const artifactSearchList: any[] = JSON.parse(artifactSearchListString);
                if (artifactSearchList.length > 0) {
                    if (this.state.removeArtifactShowMore) {
                        this.setState({ removeArtifactShowMore: false });
                    }
                    return artifactSearchList.filter((item: any) => {
                        return item.ARTIFACTID && item.ARTIFACTID !== null;
                    });
                }
                // return JSON.parse(artifactSearchListString);
            }
        }

        this.setState({ removeArtifactShowMore: true });
        return [];
    };

    /**
     * Get products count.
     * @returns Void.
     */
    private readonly _getProductsCount = (firstProduct?: ProductSearchResult): number => {
        const channelId = this.props.context.request.channel?.RecordId;

        if (firstProduct && firstProduct.ExtensionProperties && firstProduct.ExtensionProperties[0]) {
            // eslint-disable-next-line eqeqeq
            const index: number = firstProduct.ExtensionProperties?.findIndex((p: any) => p.Key == channelId);
            if (index > -1) {
                const productsCount: number = firstProduct.ExtensionProperties[index].Value?.LongValue || 0;
                if (productsCount > 0) {
                    return productsCount;
                }
            }
        }
        return 0;
    };

    /**
     * Check whether the local search refinement already exists.
     * @returns Void.
     */
    private readonly isMatchingLocalSearch = (refinementCriterion: ProductRefinerValue): boolean => {
        return (
            refinementCriterion.RefinerRecordId === 0 &&
            refinementCriterion.RefinerSourceValue === 0 &&
            refinementCriterion.DataTypeValue === 5
        );
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly searchLocal = () => {
        if (this.state.localSearchText === '') {
            return;
        }

        const { data } = this.props;
        const newLocalSearchRecord = {
            RefinerRecordId: 0,
            DataTypeValue: 5,
            LeftValueBoundString: this.state.localSearchText,
            RightValueBoundString: this.state.localSearchText,
            UnitText: '',
            RowNumber: 0,
            Count: 1,
            LeftValueBoundLocalizedString: '',
            RightValueBoundLocalizedString: '',
            ExtensionProperties: [],
            RefinerSourceValue: 0,
            SwatchImageUrl: undefined,
            SwatchColorHexCode: undefined,
            '@odata.type': '#Microsoft.Dynamics.Commerce.Runtime.DataModel.ProductRefinerValue'
        };
        // Reset resourcesSkip so that any subsequent "Show more" after searching starts from offset 0
        this.setState({ localSearchText: '', resourcesSkip: 0 });
        const currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (currentRefinementCriteria.length > 0) {
            const localSearchIndex = currentRefinementCriteria.findIndex((selectedCriterion: ProductRefinerValue) =>
                this.isMatchingLocalSearch(selectedCriterion)
            );
            console.log('localSearchIndex', localSearchIndex);

            if (localSearchIndex !== -1) {
                // Replacing an existing local search filter
                currentRefinementCriteria[localSearchIndex] = newLocalSearchRecord;
                console.log(this.props.data.listPageState);
                transaction(() => {
                    this.props.data.listPageState.result!.currentPageNumber = 0;
                    this.props.data.listPageState.result!.activeFilters = currentRefinementCriteria;
                });
                window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), currentRefinementCriteria));
            } else {
                // Adding a local search filter alongside existing refiner filters — must reset page
                // number inside a transaction so the reaction fires only once with Skip = 0
                transaction(() => {
                    this.props.data.listPageState.result!.currentPageNumber = 0;
                    this.props.data.listPageState.result!.activeFilters = [...currentRefinementCriteria, newLocalSearchRecord];
                });
                const updatedCriteria = [...currentRefinementCriteria, newLocalSearchRecord];
                window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedCriteria));
            }
        } else {
            // No existing filters — must reset page number inside a transaction so the reaction
            // fires only once with Skip = 0, not with the incremented value left by "Show more"
            transaction(() => {
                this.props.data.listPageState.result!.currentPageNumber = 0;
                this.props.data.listPageState.result!.activeFilters = [newLocalSearchRecord];
            });
            window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), [newLocalSearchRecord]));
        }
    };

    // /**
    //  * Get products dimension availabilities.
    //  * @returns Void.
    //  */
    // private readonly _onLocalSearchKeyPress = () => {};

    /**
     * Get Local search bar HTML structure.
     * @returns Void.
     */
    private readonly _getLocalSearchBar = (): any => {
        return (
            <div className='moe-local-search-container ms-search'>
                <form
                    onSubmit={e => {
                        e.preventDefault();
                    }}
                    className='moe-local-search-form ms-search__searchForm'
                    role='search'
                    aria-label='Search'
                    name='searchForm'
                >
                    <input
                        onChange={() => this.setState({ localSearchText: this.localSearchRef.current.value })}
                        ref={this.localSearchRef}
                        value={this.state.localSearchText}
                        onKeyDown={e => e.key === 'Enter' && this.searchLocal()}
                        className='moe-local-search__input'
                        type='text'
                        placeholder={this.props.config?.searchTextBoxName || 'Search resources'}
                        aria-label='Local Search Input'
                    />
                    <button
                        onClick={() => this.state.localSearchText && this.setState({ localSearchText: '' })}
                        className='moe-local-search__cancel--btn msc-btn'
                        type='button'
                        aria-label='cancel'
                    ></button>
                    <button
                        onClick={this.searchLocal}
                        className='moe-local-search__submit--btn msc-btn'
                        type='button'
                        aria-label='Submit search'
                    ></button>
                </form>
            </div>
        );
    };

    /**
     * Get products dimension availabilities.
     * @returns Void.
     */
    private readonly _getProductsDimensionsAvailabilities = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];

        const productsDimensionAvailabilities = await getDimensionAvailabilitiesForProductSearchResultAction(
            new DimensionAvailabilitiesForProductSearchResultInput(productsList, this.props.context as ICoreContext<IDimensionsApp>),
            this.props.context.actionContext
        );

        if (ArrayExtensions.hasElements(productsDimensionAvailabilities)) {
            this.setState({ productsDimensionAvailabilities });
        }
    };

    /**
     * Get the active price for the products.
     */
    private readonly _getActivePrice = async (): Promise<void> => {
        const productsList = this.props.data.listPageState.result?.activeProducts ?? [];
        const products = await getPriceForProductSearchResult(
            new PriceForProductSearchResultInput(productsList),
            this.props.context.actionContext
        );

        const listPageState = await this.props.data.listPageState;
        listPageState.activeProducts = products;
    };

    private readonly _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonTextInMaori,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private readonly _getCollectionTitle = (preferredSearchSiteLanguage: string): ITitleViewProps => {
        const { data, context, resources } = this.props;

        let collectionTitle: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q) {
            collectionTitle = `"${context.request.query.q}"`;
        } else {
            collectionTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        const collectionTitleArray: any = collectionTitle?.split('^');
        let productCountText = '';
        let productCountNumber: number | undefined;
        if (data.listPageState && data.listPageState.result && data.listPageState.result.totalProductCount !== undefined) {
            productCountNumber = data.listPageState.result.totalProductCount;
        } else if (data.products && data.products.result) {
            productCountNumber = data.products.result.count;
        }

        if (productCountNumber && productCountNumber !== 0) {
            productCountText =
                productCountNumber !== 1
                    ? format(
                          preferredSearchSiteLanguage === 'maori' ? resources.numberOfProductsInMaori : resources.numberOfProducts,
                          productCountNumber
                      )
                    : preferredSearchSiteLanguage === 'maori'
                    ? resources.oneProductInMaori
                    : resources.oneProduct;
        } else {
            productCountText = format(
                preferredSearchSiteLanguage === 'maori' ? resources.numberOfProductsInMaori : resources.numberOfProducts,
                0
            );
        }
        const pageName = context.request.telemetryPageName ? `result-count-${context.request.telemetryPageName.toLowerCase()}` : '';
        const titlePrefix = (
            <Title
                className='ms-search-result__collection-title-prefix'
                text={preferredSearchSiteLanguage === 'maori' ? resources.searchTextPrefixInMaori : resources.searchTextPrefix}
            />
        );
        const titleText = collectionTitle && (
            <Title className='ms-search-result__collection-title-text' text={collectionTitleArray[1] || collectionTitleArray[0]} />
        );
        const titleCount = <Title className='ms-search-result__collection-title-count' text={productCountText} />;

        return {
            TitleContainer: { className: `ms-search-result-container__title ${pageName}` },
            title: {
                titlePrefix,
                titleText,
                titleCount
            }
        };
    };

    private readonly _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const categoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return (
                    <Link
                        key={index}
                        text={value.Name}
                        ariaLabel={`${this.props.resources.categoryLinkAriaLabelInMaori} ${value.Name}`}
                        href={value.Url}
                    />
                );
            });
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    private readonly _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = this.props;
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        let dropdwonOptions = this.sortByDropdownOptions;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- App config settings are of "any" value.
        if (this.props.context.app.config.hideRating) {
            dropdwonOptions = this.sortByDropdownOptions.filter(dropdownOption => dropdownOption.key !== 'AverageRating-Desc');
        }
        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                labelText={
                    this.preferredSearchSiteLanguageStarting === 'maori'
                        ? resources.sortByDropdownLabelInMaori
                        : resources.sortByDropdownLabel
                }
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={dropdwonOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };

    private readonly _getMinistryPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        const skipCount =
            listPageState && listPageState.currentPageNumber !== null
                ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
                : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = (listPageState && listPageState.totalProductCount) || 0;
        const previousText = resources.flipperPreviousInMaori;
        const nextText = resources.flipperNextInMaori;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabelInMaori}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _getOchPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        const skipCount =
            listPageState && listPageState.currentPageNumber !== null
                ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
                : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = (listPageState && listPageState.totalProductCount) || 0;
        const previousText = resources.flipperPreviousInMaori;
        const nextText = resources.flipperNextInMaori;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabelInMaori}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _getExternalPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        const skipCount =
            listPageState && listPageState.currentPageNumber !== null
                ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
                : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = (listPageState && listPageState.totalProductCount) || 0;
        const previousText = resources.flipperPreviousInMaori;
        const nextText = resources.flipperNextInMaori;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabelInMaori}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    private readonly _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context, config } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const validRefiners =
            this.props.config.updateRefinerPanel && ArrayExtensions.hasElements(this.state.refiners)
                ? this.state.refiners.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  })
                : data.refiners.result &&
                  data.refiners.result.filter(refiner => {
                      return ArrayExtensions.hasElements(refiner.Values);
                  });

        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        console.log(config);

        this._getexpandRefinerCount(validRefiners && validRefiners.length, config.expandRefinersCount);
        const expandedRefiners = this.expandrefinerCount;
        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                return (
                    <RefineSubmenu
                        expandCollapseAll={this.state.expandAllRefiners}
                        ref={this.refineChild}
                        productRefinerHierarchy={productRefinerHierarchy}
                        selectedRefinerValues={activeRefiners}
                        refineItemCommonProps={this._refineItemCommonProps}
                        minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabelInMaori}
                        maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabelInMaori}
                        key={index}
                        onUpdateRefiners={this._onUpdateRefiners}
                        urlBuilder={this._buildRefinerUrl}
                        isDisabled={false}
                        isExpandedOnInitialLoad={!!(expandedRefiners && expandedRefiners > index)}
                        tempRangeTypeTODO={tempRangeTypeTODO}
                        context={context}
                        moduleId={this.props.id}
                        moduleTypeName={this.props.typeName}
                        telemetryContent={this.telemetryContent}
                        productCountAriaLabel={this.props.resources.productCountAriaLabelInMaori}
                        singleProductCountAriaLabel={this.props.resources.singleProductCountAriaLabelInMaori}
                        isMobile={this.isMobile}
                        config={config}
                    />
                );
            });

        return {
            RefineMenuContainer: { className: 'ms-search-result-container__refine-menu' },
            RefinerSectionContainer: { className: 'ms-search-result-container__refiner-section' },
            refiners: subMenus
        };
    };

    private readonly _getChoiceSummary = (): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        return (
            <ChoiceSummary
                classNames='ms-choice-summary-by-category'
                clearAllText={resources.clearAllTextInMaori}
                label={resources.choiceSummaryLabelInMaori}
                closeAriaLabel={resources.closeAriaLabelInMaori}
                selectedChoices={selectedRefiners}
                // @ts-expect-error: NOTE Type-unsafe line below, null refinersByCategoryHierarchy case not handled
                refinerHierarchy={data.refiners.result}
                choiceFormat={resources.choiceFormatInMaori}
                choiceRangeValueFormat={resources.choiceRangeValueFormatInMaori}
                telemetry={telemetry}
                onChoiceClicked={this._onChoiceClicked}
                urlBuilder={this._buildRefinerUrlForChoiceSummary}
                choiceAriaLabel={resources.choiceAriaLabelInMaori}
                telemetryContent={this.telemetryContent}
                context={this.props.context}
            />
        );
    };

    private readonly _getProducts = (preferredLanguageForError: string): any => {
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { resources } = this.props;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const allProducts: any = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        // if (allProducts.length === 0) {
        //     allProducts = (this.props.data.products.result && this.props.data.products.result?.products) || [];
        // }

        const quickviewslot = this.props.slots && this.props.slots.quickview && this.props.slots.quickview[0];
        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        if (allProducts.length === 0) {
            if (!this.state.removeResourceShowMore) {
                this.setState({ removeResourceShowMore: true });
            }
            if (this.state.enableListViewToggle) {
                this.setState({ enableListViewToggle: false });
            }
        } else {
            if (!this.state.enableListViewToggle) {
                this.setState({ enableListViewToggle: true });
            }
        }

        // this.props.data.listPageState.then(listPageState => {
        //     if (listPageState.activeProducts.length === 0 && allProducts.products.length > 0) {
        //         if (this._pageType === 'Category') {
        //             listPageState.pageType = 'Category';
        //         } else {
        //             listPageState.pageType = 'Search';
        //         }

        //         let querySorting: SortColumn[] = [];

        //         if (this.props.context.request.query && this.props.context.request.query.sorting) {
        //             querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        //         }

        //         listPageState.currentPageNumber =
        //             (this.props.context.request.query && +this.props.context.request.query.skip / (this.props.config.itemsPerPage || 10)) ||
        //             0;
        //         listPageState.sortingCritera = { Columns: querySorting };
        //         listPageState.pageSize = this.props.config.itemsPerPage || 10;
        //         listPageState.activeProducts = allProducts.products;
        //         // listPageState.totalProductCount = products.count;
        //         listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);
        //         listPageState.artifactsData = this._getAllArtifactsData(allProducts?.products[0]);
        //         listPageState.totalProductCount = this._getProductsCount(allProducts?.products[0]);
        //     }

        //     // const currPageNumber = listPageState.currentPageNumber ? listPageState.currentPageNumber + 1 : 1;
        //     // const finalIndex = currPageNumber * (listPageState.pageSize || 10);
        //     if (allProducts.length < this._getProductsCount(allProducts?.products[0])) {
        //         if (this.state.removeResourceShowMore) {
        //             this.setState({ removeResourceShowMore: false });
        //         }
        //     }
        //     if (allProducts.length >= this._getProductsCount(allProducts?.products[0])) {
        //         if (!this.state.removeResourceShowMore) {
        //             this.setState({ removeResourceShowMore: true });
        //         }
        //     }
        // });

        console.log('Channel products', allProducts);

        return allProducts.length !== 0 ? (
            <ProductSearchResultItems
                products={allProducts}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                allowBackNavigation={allowBack}
                telemetryContent={this.telemetryContent}
                quickviewSlot={quickviewslot}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                productsDimensionAvailabilities={this.state.productsDimensionAvailabilities}
            />
        ) : (
            <div className='msc-alert msc-alert-warning'>
                {this.props.config.siteName === KWR
                    ? preferredLanguageForError === 'maori'
                        ? resources.clickFiltersTextInMaori
                        : resources.clickFiltersText
                    : preferredLanguageForError === 'maori'
                    ? resources.resultSearchNotFoundTextInMaori
                    : resources.resultSearchNotFoundText}
            </div>
        );
    };

    private readonly _getListViewProducts = (preferredLanguageForError: string): JSX.Element => {
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { resources } = this.props;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        let allProducts: any = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        if (allProducts.length === 0) {
            allProducts = (this.props.data.products.result && this.props.data.products.result?.products) || [];
        }

        const quickviewslot = this.props.slots && this.props.slots.quickview && this.props.slots.quickview[0];
        const isPriceMinMaxFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        if (allProducts.length === 0) {
            if (!this.state.removeResourceShowMore) {
                this.setState({ removeResourceShowMore: true });
            }
        }

        // this.props.data.listPageState.then(listPageState => {
        //     // const currPageNumber = listPageState.currentPageNumber ? listPageState.currentPageNumber + 1 : 1;
        //     // const finalIndex = currPageNumber * (listPageState.pageSize || 10);
        //     // if (allProducts.length < listPageState?.totalProductCount) {
        //     //     if (this.state.removeResourceShowMore) {
        //     //         this.setState({ removeResourceShowMore: false });
        //     //     }
        //     // }
        //     // if (allProducts.length >= listPageState.totalProductCount) {
        //     //     if (!this.state.removeResourceShowMore) {
        //     //         this.setState({ removeResourceShowMore: true });
        //     //     }
        //     // }
        //     // products = products.slice(0, finalIndex);
        // });

        return allProducts.length !== 0 ? (
            <ProductSearchResultItems
                listView={true}
                products={allProducts}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                allowBackNavigation={allowBack}
                telemetryContent={this.telemetryContent}
                quickviewSlot={quickviewslot}
                channelInventoryConfigurationId={this.props.data.products.result?.channelInventoryConfigurationId}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                productsDimensionAvailabilities={this.state.productsDimensionAvailabilities}
            />
        ) : (
            <div className='msc-alert msc-alert-warning'>
                {preferredLanguageForError === 'maori' ? resources.resultSearchNotFoundTextInMaori : resources.resultSearchNotFoundText}.
            </div>
        );
    };

    private readonly _getCuratedCollectionsData = (preferredLanguageForError: string): ICuratedCollection[] => {
        const allProducts = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];

        const products = allProducts.filter(product => {
            const index: any = product.ExtensionProperties?.findIndex(p => p.Key === 'RelatedProductCount');
            return (
                index > -1 &&
                product &&
                product?.ExtensionProperties &&
                product?.ExtensionProperties?.length > 0 &&
                product?.ExtensionProperties[index]?.Value?.StringValue !== ''
            );
        });
        const curatedProducts: ICuratedCollection[] = [];

        products &&
            products.forEach(product => {
                const productUrl = product
                    ? getProductPageUrlSync(product.Name ?? '', product.RecordId, this.props.context.actionContext, undefined)
                    : '';
                const imageURL = generateImageUrl(product.PrimaryImageUrl, this.props.context.request.apiSettings) || '';
                const index: any = product?.ExtensionProperties?.findIndex(p => p.Key === 'RelatedProductCount');
                const channelIndex: any = product?.ExtensionProperties?.findIndex(p => p.Key === 'ChannelId');
                if (index > -1) {
                    const curatedProduct: ICuratedCollection = {
                        recordId: product.RecordId,
                        Count:
                            (product &&
                                product?.ExtensionProperties &&
                                product?.ExtensionProperties?.length > 0 &&
                                product.ExtensionProperties[index].Value?.StringValue) ||
                            '0',
                        Description: product.Description || '',
                        Name: product.Name || '',
                        productURL: productUrl,
                        imageURL: imageURL,
                        channelId:
                            (channelIndex > -1 &&
                                product?.ExtensionProperties &&
                                product?.ExtensionProperties?.length > 0 &&
                                product.ExtensionProperties[channelIndex].Value?.LongValue) ||
                            0
                    };
                    curatedProducts.push(curatedProduct);
                }
            });

        const kwrCuratedProducts = curatedProducts.filter(
            curatedProduct => curatedProduct.channelId === this.props.context.request.channel?.RecordId
        );

        return kwrCuratedProducts;
    };

    private readonly _getOchProducts = (preferredLanguageForError: string, preferredSearchSiteLanguage: string): JSX.Element => {
        const { context, data, config, resources } = this.props;
        // let allProducts = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        // if (allProducts.length === 0) {
        //     allProducts = (this.props.data.products.result && this.props.data.products.result?.products) || [];
        // }

        // Construct Base Search URL
        let searchTitle: string | undefined;
        let searchTitleForUrl: string | undefined;

        if (context && context.request && context.request.query && context.request.query.q) {
            searchTitle = `"${context.request.query.q}"`;
            searchTitleForUrl = context.request.query.q;
        } else {
            searchTitle =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);

            searchTitleForUrl = searchTitle;
        }

        // moeSearchProductsAsync(context.actionContext as any, { SearchCondition: searchTitle }, true);

        // Encode the URL.
        const encodedSearchTitleForUrl = encodeURIComponent(searchTitleForUrl || '');

        const nzcSearchUrl = `${config?.nzcSearchUrl?.split('&q')[0]}&q=${encodedSearchTitleForUrl}`;
        const twkSearchUrl = `${config?.twkSearchUrl?.split('&q')[0]}&q=${encodedSearchTitleForUrl}`;
        const kohSearchUrl = `${config?.kohSearchUrl?.split('&q')[0]}&q=${encodedSearchTitleForUrl}`;
        const kwrSearchUrl = `${config?.kwrSearchUrl?.split('&q')[0]}&q=${encodedSearchTitleForUrl}`;

        // Get channel resources count information
        const channelResourcesData: any = [];
        let channelSearchCountInfo: any = [];

        if (this.state.channelData.length > 0) {
            this.state.channelData.map((channelData: IMOEChannel): any => {
                // eslint-disable-next-line eqeqeq
                const index: any = this.state.tahurangiSearchCount?.findIndex(p => p.Id == channelData?.Id?.toString());

                if (index > -1) {
                    channelResourcesData.push({
                        channelId: channelData.Id,
                        channelName: channelData.Name,
                        resourcesCount:
                            this.state.tahurangiSearchCount &&
                            this.state.tahurangiSearchCount[index] &&
                            this.state.tahurangiSearchCount[index]?.Count
                    });
                }
            });

            console.log('channelResourcesData', channelResourcesData);

            if (channelResourcesData.length > 0) {
                channelResourcesData.map((item: any) => {
                    if (item.channelName.includes('ealand')) {
                        channelSearchCountInfo.push({ ...item, searchUrl: nzcSearchUrl });
                    }

                    if (item.channelName.includes('hildhood')) {
                        channelSearchCountInfo.push({ ...item, searchUrl: twkSearchUrl });
                    }

                    if (item.channelName.includes('whata')) {
                        channelSearchCountInfo.push({ ...item, searchUrl: kwrSearchUrl });
                    }

                    if (item.channelName.includes('hanga')) {
                        channelSearchCountInfo.push({ ...item, searchUrl: kohSearchUrl });
                    }
                });
            }

            channelSearchCountInfo = channelSearchCountInfo.filter((item: any) => {
                return item.channelId !== context.request.apiSettings.channelId;
            });

            if (channelSearchCountInfo.length > 0) {
                return (
                    <ul className='moe-och-srchresults'>
                        {channelSearchCountInfo.map((channelSearchCountInfoItem: any) => {
                            if (channelSearchCountInfoItem.channelId !== context.request.apiSettings.channelId) {
                                const resultText = resources.ochSearchResultsText
                                    .replace('{0}', channelSearchCountInfoItem.resourcesCount)
                                    .replace('{1}', searchTitle as any)
                                    .replace('{2}', channelSearchCountInfoItem.channelName);
                                const resultTextInMaori = resources.ochSearchResultsTextInMaori
                                    .replace('{0}', channelSearchCountInfoItem.resourcesCount)
                                    .replace('{1}', searchTitle as any)
                                    .replace('{2}', channelSearchCountInfoItem.channelName);
                                return (
                                    <li className='moe-och-srchresults__item' key={channelSearchCountInfoItem.channelId}>
                                        <a
                                            className='moe-och-srchresults__link'
                                            href={channelSearchCountInfoItem.searchUrl}
                                            rel='noreferrer'
                                            target='_blank'
                                        >
                                            <span>{preferredSearchSiteLanguage === 'maori' ? resultTextInMaori : resultText}</span>{' '}
                                            <FaArrowRight className='moe-och-srchresults__link-icn' />
                                        </a>
                                    </li>
                                );
                            } else {
                                return null;
                            }
                        })}
                    </ul>
                );
            } else {
                return (
                    <div className='msc-alert msc-alert-warning'>
                        {preferredSearchSiteLanguage === 'maori'
                            ? resources.resultSearchNotFoundTextInMaori
                            : resources.resultSearchNotFoundText}
                    </div>
                );
            }
        }

        return (
            <div className='msc-alert msc-alert-warning'>
                {preferredSearchSiteLanguage === 'maori' ? resources.resultSearchNotFoundTextInMaori : resources.resultSearchNotFoundText}
            </div>
        );
    };

    private readonly _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        window.history.pushState(
            {},
            '',
            buildListPageUrl(
                getCurrentUrl(this.props.context.request),
                undefined,
                [this._getSortColumnFromSelectedOption(notification.selectedOption)],
                undefined
            )
        );
        if (this.state.removeResourceShowMore) {
            this.setState({ ...this.state, removeResourceShowMore: false, resourcesSkip: 0 });
        }
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
        });
    };

    private readonly _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;

        if (!data.searchConfiguration.result) {
            return {};
        }

        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });

        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }

        return {};
    };

    /**
     * Retrieves current sort criteria.
     * @returns Sort criteria column which is currently active or undefined if it's not specified.
     */
    private readonly _getSortCriteriaColumn = (): SortColumn | undefined => {
        const sortCriteria = this.props.data.listPageState.result?.sortingCritera;
        if (ArrayExtensions.hasElements(sortCriteria?.Columns)) {
            return sortCriteria!.Columns[0];
        }

        const queryColumns = parseQueryParam<SortColumn[]>('sorting', this.props.context.request);
        if (ArrayExtensions.hasElements(queryColumns)) {
            return queryColumns[0];
        }

        return undefined;
    };

    /**
     * Retrieves current selected dropdown option based on the sort criteria.
     * @returns Current dropdown option.
     */
    private readonly _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const activeSortColumn = this._getSortCriteriaColumn();

        const searchConfiguration = this.props.data.searchConfiguration.result;

        if (!activeSortColumn || !searchConfiguration) {
            return undefined;
        }

        const activeMappedConfig = searchConfiguration.find(
            mappedSearchConfig =>
                mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
        );
        if (!activeMappedConfig) {
            return undefined;
        }

        return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
    };

    private readonly _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        console.log('itemToggleNotification', itemToggleNotification);
        const { context } = this.props;
        const requestContext = context && context.request;
        const actionContext = context && context.actionContext;

        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: ${!requestContext ? 'requestContext ' : ''} ${
                !actionContext ? 'actionContext ' : ''
            } could not be found`;
            this.props.telemetry.warning(error);
        }

        const updatedRefinementCriteria = getUpdatedRefinementCriteria(
            itemToggleNotification,
            (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || []
        );

        console.log('updatedRefinementCriteria', updatedRefinementCriteria);

        window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private readonly _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        console.log(itemToggleNotification);
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                this.props.data.listPageState.result.activeFilters || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        }
        this.props.telemetry.warning('[buildRefinerQueryString]List Page State Not available, unable to build refiner URL');
        return '';
    };

    private readonly _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        const { data, telemetry } = this.props;

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!notification.clearAll) {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];

            // Set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = dropdownElementId && document.getElementById(dropdownElementId);
            setTimeout(() => {
                dropdownElement && dropdownElement.focus();
            }, 50);
        }

        const fullUrl = getCurrentUrl(this.props.context.request);
        window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
        data.listPageState.result.currentPageNumber = 0;
        data.listPageState.result.activeFilters = currentRefinementCriteria;
    };

    private readonly _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    }

    private _toggleModal(): void {
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();

        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    private readonly _getexpandRefinerCount = (validRefiners?: number, refinerCount?: expandRefinersCount): void => {
        switch (refinerCount) {
            case expandRefinersCount.all:
                this.expandrefinerCount = validRefiners;
                break;
            case expandRefinersCount.one:
                this.expandrefinerCount = 1;
                break;
            case expandRefinersCount.two:
                this.expandrefinerCount = 2;
                break;
            case expandRefinersCount.three:
                this.expandrefinerCount = 3;
                break;
            case expandRefinersCount.four:
                this.expandrefinerCount = 4;
                break;
            default:
                this.expandrefinerCount = 0;
        }
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Renders a Expand collapse all in refine Menu.
     * @param  None - No Paramters.
     * @returns Expand Collapse all JSX.
     */
    private readonly _renderCollapseExpandAll = (preferredSearchSiteLanguage: string) => {
        return (
            <div className='ms-search-result-container__refiner-section-expand__collapse'>
                <button className='ms-search-result-container__refiner-section-expand-btn' onClick={() => this._expandCollapseAll(true)}>
                    {preferredSearchSiteLanguage === 'maori'
                        ? this.props.resources.expandAllTextInMaori
                        : this.props.resources.expandAllText}
                </button>
                <span className='expand-all-seperator'>|</span>
                <button className='ms-search-result-container__refiner-section-collapse-btn' onClick={() => this._expandCollapseAll(false)}>
                    {preferredSearchSiteLanguage === 'maori'
                        ? this.props.resources.collapseAllTextInMaori
                        : this.props.resources.collapseAllText}
                </button>
            </div>
        );
    };

    /**
     * Sets expand collapse All State.
     * @param  refineExpandState - Refinement state for expand and collapse all.
     * @returns Nothing.
     */
    private readonly _expandCollapseAll = (refineExpandState: boolean) => {
        this.setState({ expandAllRefiners: refineExpandState });
    };
}
